// client/src/App.js
import './App.css'
import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import 'toastr/build/toastr.min.css'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from './queryClient'
import featureFlags, { initFeatureFlags } from './config/featureFlags'
import { track } from './analytics'

import Landing from './components/Landing'
import Signin from './components/auth/Signin'
import Signup from './components/auth/Signup'
import VerifyEmail from './components/auth/VerifyEmail'
import VerifyEmailPending from './components/auth/VerifyEmailPending'
import ForgotPassword from './components/auth/ForgotPassword'
import ResetPassword from './components/auth/ResetPassword'
import Dashboard from './components/dashboard/Dashboard'
import ModernProfile from './components/auth/ModernProfile'
import CompleteProfile from './components/auth/CompleteProfile'
import AdminDashboard from './components/admin/AdminDashboard'
import AdminUsers from './components/admin/AdminUsers'
import AdminAnalytics from './components/admin/AdminAnalytics'
import AdminInviteCodes from './components/admin/AdminInviteCodes'
import AdminDatabaseTools from './components/admin/AdminDatabaseTools'
import AdminMonitoring from './components/admin/AdminMonitoring'
import AdminMonitoringDigest from './components/admin/AdminMonitoringDigest'
import AdminAwardGrant from './components/admin/AdminAwardGrant'
import AdminAwardSettings from './components/admin/AdminAwardSettings'
import AdminAwardDefinitions from './components/admin/AdminAwardDefinitions'
import AdminMatchesView from './components/admin/AdminMatchesView'
import AdminAvailabilityView from './components/admin/AdminAvailabilityView'
import AdminProposalsView from './components/admin/AdminProposalsView'
import AdminEmailOutbox from './components/admin/AdminEmailOutbox'
import AdminEmailTemplateTester from './components/admin/AdminEmailTemplateTester'
import AdminSmsTester from './components/admin/AdminSmsTester'
import AdminLeagueEmailSettings from './components/admin/AdminLeagueEmailSettings'
import NotificationDashboard from './components/admin/NotificationDashboard'
import AdminFeatureFlags from './components/admin/AdminFeatureFlags'
import RegistrationFlow from './components/leagues/RegistrationFlow'
import AdminLeagueView from './components/admin/AdminLeagueView'
import CreateLeague from './components/admin/CreateLeague'
import MyLeagues from './components/leagues/MyLeagues'
import MatchScoreEntry from './components/leagues/MatchScoreEntry'
import MatchScheduler from './components/leagues/MatchScheduler'
import RequireAuth from './components/auth/RequireAuth'
import RequireAdmin from './components/auth/RequireAdmin'
import RequireContactInfo from './components/auth/RequireContactInfo'
import LeagueLanding from './components/leagues/LeagueLanding'
import useAuth from './hooks/useAuth'
import LeagueStandings from './components/leagues/LeagueStandings'
import LeagueMatches from './components/leagues/LeagueMatches'
import AvailabilityForm from './components/scheduling/AvailabilityForm'
import AvailabilityList from './components/scheduling/AvailabilityList'
import ScheduleOverlaps from './components/scheduling/ScheduleOverlaps'
import WeeklyCalendar from './components/scheduling/WeeklyCalendar'
import MonthCalendarPage from './components/scheduling/MonthCalendarPage'
import ErrorBoundary from './components/ui/ErrorBoundary'
import FeedbackForm from './components/feedback/FeedbackForm'
import AdminFeedback from './components/admin/AdminFeedback'
import AdminInterestedUsers from './components/admin/AdminInterestedUsers'
import ComingSoon from './components/ComingSoon'
import AdminCourts from './components/admin/AdminCourts'
import ImpersonationBanner from './components/admin/ImpersonationBanner'
import PlayerHistory from './components/players/PlayerHistory'
import Chat from './components/chat/Chat'
import TermsOfService from './components/TermsOfService'
import PrivacyPolicy from './components/PrivacyPolicy'
import FoundersPage from './components/FoundersPage'
import SMSNotificationsDisclosure from './components/SMSNotificationsDisclosure'
import Footer from './components/common/Footer'
import TrophyCasePage from './components/awards/TrophyCasePage'
import NotificationsPage from './components/notifications/NotificationsPage'
import NotificationSettings from './components/notifications/NotificationSettings'
import SkipLink from './components/common/SkipLink'
import InterestForm from './components/public/InterestForm'
import ConfirmPartnerPage from './components/public/ConfirmPartnerPage'
import SmsActionPage from './components/sms/SmsActionPage'
import { initializeTheme } from './utils/themeUtils'
import { isOwner } from './utils/userRoles'
import { isProdServeSyncRuntime } from './utils/environmentHosts'

const MatchProposalsInbox = lazy(
  () => import('./components/matches/MatchProposalsInbox'),
)
const ScheduledMatches = lazy(
  () => import('./components/matches/ScheduledMatches'),
)

function RootRoute() {
  const { isLoggedIn, loading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!loading && !isLoggedIn) {
      navigate('/landing')
    }
  }, [isLoggedIn, loading, navigate])

  if (loading) {
    return <div>Loading...</div>
  }

  if (!isLoggedIn) {
    return null // Will redirect to landing
  }

  return (
    <RequireAuth>
      <Dashboard />
    </RequireAuth>
  )
}

function RequireDatabaseToolsAccess({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <p>Loading...</p>
  }

  if (isProdServeSyncRuntime() && !isOwner(user)) {
    return <p>Access denied. Owner access required on production.</p>
  }

  return children
}

function App() {
  const location = useLocation()
  const previousPathRef = useRef(null)
  // Initialize Stripe lazily - only loads when needed
  const [stripePromise, setStripePromise] = useState(null)
  const [flagsLoaded, setFlagsLoaded] = useState(false)

  // Initialize theme on app mount
  useEffect(() => {
    initializeTheme()
  }, [])

  // Initialize feature flags from backend API
  useEffect(() => {
    initFeatureFlags().then(() => setFlagsLoaded(true))
  }, [])

  useEffect(() => {
    track('page.view', {
      page: location.pathname,
      previous_page: previousPathRef.current,
    })
    previousPathRef.current = location.pathname
  }, [location.pathname])

  useEffect(() => {
    // Load Stripe publishable key from environment or use empty string for graceful handling
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    if (publishableKey) {
      console.log(
        '[Stripe] Initializing with publishable key:',
        publishableKey.substring(0, 15) + '...',
      )
      setStripePromise(loadStripe(publishableKey))
      console.log('[Stripe] Stripe promise created successfully')
    } else {
      console.warn(
        '[Stripe] No publishable key found - Stripe will not be initialized',
      )
    }
  }, [])

  // Show loading spinner while feature flags are being fetched
  if (!flagsLoaded) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '100vh' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div className="App">
        <SkipLink />
        <ImpersonationBanner />
        <div className="app-layout">
          <div className="app-content" id="main-content">
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/landing" element={<Landing />} />
              <Route path="/interest" element={<InterestForm />} />
              <Route path="/sign-in" element={<Signin />} />
              <Route path="/sign-up" element={<Signup />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/confirm-partner" element={<ConfirmPartnerPage />} />
              <Route path="/a/:token" element={<SmsActionPage />} />
              <Route
                path="/verify-email-pending"
                element={<VerifyEmailPending />}
              />
              <Route
                path="/complete-profile"
                element={
                  <RequireAuth>
                    <CompleteProfile />
                  </RequireAuth>
                }
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/profile"
                element={
                  <RequireAuth>
                    <ModernProfile />
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminLeagueView />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminDashboard />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/database-tools"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <RequireDatabaseToolsAccess>
                        <AdminDatabaseTools />
                      </RequireDatabaseToolsAccess>
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminUsers />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/invite-codes"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminInviteCodes />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminAnalytics />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/monitoring"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminMonitoring />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/monitoring/digest"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminMonitoringDigest />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/feature-flags"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminFeatureFlags />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              {/* SMS Notifications Dashboard - Feature Flag Gated */}
              {featureFlags.SMS_NOTIFICATIONS && (
                <Route
                  path="/admin/notifications"
                  element={
                    <RequireAuth>
                      <RequireAdmin>
                        <NotificationDashboard />
                      </RequireAdmin>
                    </RequireAuth>
                  }
                />
              )}
              <Route
                path="/admin/courts"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminCourts />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/grant-award"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminAwardGrant />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/award-settings"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminAwardSettings />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/award-definitions"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminAwardDefinitions />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/matches"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminMatchesView />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/availability"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminAvailabilityView />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/proposals"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminProposalsView />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/email-outbox/:leagueId?"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminEmailOutbox />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/email-template-tester"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminEmailTemplateTester />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/test-sms"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminSmsTester />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/league/:leagueId/email-settings"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminLeagueEmailSettings />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/register"
                element={
                  <RequireAuth>
                    {stripePromise ? (
                      <Elements stripe={stripePromise}>
                        <RegistrationFlow />
                      </Elements>
                    ) : (
                      <RegistrationFlow />
                    )}
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/leagues-registrations"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminLeagueView />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/leagues/editor/:leagueId?"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <CreateLeague />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/my-leagues"
                element={
                  <RequireAuth>
                    <MyLeagues />
                  </RequireAuth>
                }
              />
              <Route
                path="/chat"
                element={
                  <RequireAuth>
                    <Chat />
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues/:leagueId"
                element={
                  <RequireAuth>
                    <LeagueLanding />
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues/:leagueId/schedule"
                element={
                  <RequireAuth>
                    <RequireContactInfo>
                      <WeeklyCalendar />
                    </RequireContactInfo>
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues/:leagueId/schedule-month"
                element={
                  <RequireAuth>
                    <RequireContactInfo>
                      <MonthCalendarPage />
                    </RequireContactInfo>
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues/:leagueId/find-matches"
                element={
                  <RequireAuth>
                    <RequireContactInfo>
                      <ScheduleOverlaps />
                    </RequireContactInfo>
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues/:leagueId/availability"
                element={
                  <RequireAuth>
                    <RequireContactInfo>
                      <WeeklyCalendar />
                    </RequireContactInfo>
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues/:leagueId/availability/list"
                element={
                  <RequireAuth>
                    <RequireContactInfo>
                      <AvailabilityList />
                    </RequireContactInfo>
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues/:leagueId/availability/add"
                element={
                  <RequireAuth>
                    <RequireContactInfo>
                      <AvailabilityForm />
                    </RequireContactInfo>
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues/:leagueId/match/:matchId/score"
                element={
                  <RequireAuth>
                    <MatchScoreEntry />
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues/:leagueId/match/:matchId/schedule"
                element={
                  <RequireAuth>
                    <RequireContactInfo>
                      <MatchScheduler />
                    </RequireContactInfo>
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues/:leagueId/proposals"
                element={
                  <RequireAuth>
                    <RequireContactInfo>
                      <ErrorBoundary>
                        <Suspense fallback={<div>Loading proposals...</div>}>
                          <MatchProposalsInbox />
                        </Suspense>
                      </ErrorBoundary>
                    </RequireContactInfo>
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues/:leagueId/standings"
                element={
                  <RequireAuth>
                    <LeagueStandings />
                  </RequireAuth>
                }
              />
              <Route
                path="/leagues/:leagueId/matches"
                element={
                  <RequireAuth>
                    <LeagueMatches />
                  </RequireAuth>
                }
              />
              <Route
                path="/players/:playerId/history"
                element={
                  <RequireAuth>
                    <PlayerHistory />
                  </RequireAuth>
                }
              />
              <Route
                path="/availability"
                element={
                  <RequireAuth>
                    <AvailabilityList />
                  </RequireAuth>
                }
              />
              <Route
                path="/availability/add"
                element={
                  <RequireAuth>
                    <AvailabilityForm />
                  </RequireAuth>
                }
              />
              <Route
                path="/match-proposals"
                element={
                  <RequireAuth>
                    <ErrorBoundary>
                      <Suspense fallback={<div>Loading...</div>}>
                        <MatchProposalsInbox />
                      </Suspense>
                    </ErrorBoundary>
                  </RequireAuth>
                }
              />
              <Route
                path="/scheduled-matches"
                element={
                  <RequireAuth>
                    <ErrorBoundary>
                      <Suspense fallback={<div>Loading...</div>}>
                        <ScheduledMatches />
                      </Suspense>
                    </ErrorBoundary>
                  </RequireAuth>
                }
              />
              <Route
                path="/feedback"
                element={
                  <RequireAuth>
                    <FeedbackForm />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/feedback"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminFeedback />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/interested-users"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminInterestedUsers />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/trophy-case"
                element={
                  <RequireAuth>
                    <TrophyCasePage />
                  </RequireAuth>
                }
              />
              <Route
                path="/notifications"
                element={
                  <RequireAuth>
                    <NotificationsPage />
                  </RequireAuth>
                }
              />
              {featureFlags.SMS_NOTIFICATIONS ? (
                <Route
                  path="/notification-settings"
                  element={
                    <RequireAuth>
                      <NotificationSettings />
                    </RequireAuth>
                  }
                />
              ) : (
                <Route
                  path="/notification-settings"
                  element={
                    <RequireAuth>
                      <ComingSoon />
                    </RequireAuth>
                  }
                />
              )}
              <Route path="/coming-soon" element={<ComingSoon />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/founders" element={<FoundersPage />} />
              <Route
                path="/sms-notifications-disclosure"
                element={<SMSNotificationsDisclosure />}
              />
            </Routes>
          </div>
          <Footer />
        </div>
        {/* React Query DevTools - only in development */}
        {process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
        )}
      </div>
    </QueryClientProvider>
  )
}

export default App
